import { Navigate } from "react-router";
import type { AppRouteObject } from "./types";
import { lazyRoute } from "./lazyRoute";
import { FontAwesomeIcons } from "src/constants/fontAwesomeIcons";
import * as Dtos from "src/dtos/Tmd.dtos";

export const trialRoutes = {
    path: "trials",
    handle: {
        title: "Trials",
        help: null,
        access: { permission: Dtos.Permission.TrialView },
        breadcrumb: { label: "Trials", icon: FontAwesomeIcons.Duotone.TRIAL, url: "/trials" },
        navigation: { title: "Trials", icon: FontAwesomeIcons.Duotone.TRIAL, primary: true, drawer: true, order: 1 },
    },
    children: [
        {
            index: true,
            lazy: lazyRoute(() => import("../containers/Trials/TrialsPage/TrialsPage")),
            handle: {
                title: "Trials",
                help: { topicKey: "trials" },
                access: { permission: Dtos.Permission.TrialView },
                breadcrumb: { label: "Trials", icon: FontAwesomeIcons.Duotone.TRIAL, url: "/trials" },
            }
        },
        {
            path: "create",
            lazy: lazyRoute(() => import("../containers/Trials/Trial/TrialPage"), { mode: "create" }),
            handle: {
                title: "Create Trial",
                help: { topicKey: "trial" },
                access: { permission: Dtos.Permission.TrialCreate },
                breadcrumb: { label: "Create", icon: FontAwesomeIcons.Duotone.SQUAREPLUS, url: "/trials/create" },
            }
        },
        {
            path: ":trialId",
            handle: {
                title: ({ labels }) => labels.trialName ?? "Trial",
                help: null,
                breadcrumb: { label: ({ labels }) => labels.trialName ?? "Trial", icon: FontAwesomeIcons.Duotone.DICEONE, url: ({ params }) => `/trials/${params.trialId}/details` },
                access: { permission: Dtos.Permission.TrialView, trialParam: "trialId" },
            },
            children: [
                {
                    index: true,
                    element: <Navigate replace to="details" />,
                    handle: { title: ({ labels }) => labels.trialName ?? "Trial", help: null, breadcrumb: null }
                },
                {
                    path: "details",
                    lazy: lazyRoute(() => import("../containers/Trials/Trial/TrialPage"), { mode: "edit" }),
                    handle: {
                        title: ({ labels }) => labels.trialName ?? "Trial",
                        help: { topicKey: "trial" },
                        access: { permission: Dtos.Permission.TrialView, trialParam: "trialId" },
                        breadcrumb: { label: "Details", icon: FontAwesomeIcons.Duotone.CIRCLEINFO, url: ({ params }) => `/trials/${params.trialId}/details` },
                    }
                },
                {
                    path: "institutions",
                    handle: {
                        title: "Institutions",
                        help: null,
                        access: { permission: Dtos.Permission.TrialInstitutionView, trialParam: "trialId" },
                        breadcrumb: { label: "Institutions", icon: FontAwesomeIcons.Duotone.INSTITUTION, url: ({ params }) => `/trials/${params.trialId}/institutions` },
                        navigation: { title: "Institutions", icon: FontAwesomeIcons.Duotone.INSTITUTION, drawer: true },
                    },
                    children: [
                        {
                            index: true,
                            lazy: lazyRoute(() => import("../containers/Trials/TrialInstitutions/TrialInstitutionsPage")),
                            handle: {
                                title: "Institutions",
                                help: { topicKey: "trial-institutions" },
                                access: { permission: Dtos.Permission.TrialInstitutionView, trialParam: "trialId" },
                                breadcrumb: null,
                            }
                        },
                    ],
                },
                {
                    path: "trialinstitution",
                    handle: {
                        title: "Trial Institution",
                        help: null,
                        access: { permission: Dtos.Permission.TrialInstitutionView, trialParam: "trialId" },
                        breadcrumb: { label: "Institutions", icon: FontAwesomeIcons.Duotone.INSTITUTION, url: ({ params }) => `/trials/${params.trialId}/institutions` },
                    },
                    children: [
                        {
                            path: "create/details",
                            lazy: lazyRoute(() => import("../containers/TrialInstitution/TrialInstitution/TrialInstitutionPageSWR"), { mode: "create" }),
                            handle: {
                                title: "Create Trial Institution",
                                help: { topicKey: "trial-institution" },
                                access: { permission: Dtos.Permission.TrialInstitutionCreate, trialParam: "trialId" },
                                breadcrumb: { label: "Create", icon: FontAwesomeIcons.Duotone.SQUAREPLUS, url: ({ params }) => `/trials/${params.trialId}/trialinstitution/create/details` },
                            }
                        },
                        {
                            path: ":trialInstitutionId",
                            handle: {
                                title: ({ labels }) => labels.trialInstitutionName ?? "Trial Institution",
                                help: null,
                                access: { permission: Dtos.Permission.TrialInstitutionView, trialParam: "trialId", institutionParam: "trialInstitutionId" },
                                breadcrumb: { label: ({ labels }) => labels.trialInstitutionName ?? "Institution", icon: FontAwesomeIcons.Duotone.BUILDING, url: ({ params }) => `/trials/${params.trialId}/trialinstitution/${params.trialInstitutionId}/details` },
                            },
                            children: [
                                {
                                    index: true,
                                    element: (
                                        <Navigate replace to="details" />
                                    ),
                                    handle: { title: ({ labels }) => labels.trialInstitutionName ?? "Trial Institution", help: null, breadcrumb: null }
                                },
                                {
                                    path: "details",
                                    lazy: lazyRoute(() => import("../containers/TrialInstitution/TrialInstitution/TrialInstitutionPageSWR"), { mode: "edit" }),
                                    handle: {
                                        title: ({ labels }) => labels.trialInstitutionName ?? "Trial Institution",
                                        help: { topicKey: "trial-institution" },
                                        access: { permission: Dtos.Permission.TrialInstitutionView, trialParam: "trialId", institutionParam: "trialInstitutionId" },
                                        breadcrumb: null,
                                    }
                                },
                                {
                                    path: "items",
                                    lazy: lazyRoute(() => import("../containers/TrialInstitution/TrialInstitutionItems/TrialInstitutionItemsPageSWR")),
                                    handle: { help: { topicKey: "trial-institution-items" } }
                                },
                                {
                                    path: "personnel",
                                    handle: { help: null },
                                    children: [
                                        {
                                            index: true,
                                            lazy: lazyRoute(() => import("../containers/TrialInstitution/TrialInstitutionPersonnels/TrialInstitutionPersonnelsPage")),
                                            handle: { help: { topicKey: "trial-institution-personnels" } }
                                        },
                                        {
                                            path: "create",
                                            lazy: lazyRoute(() => import("../containers/TrialInstitution/TrialInstitutionPersonnel/TrialInstitutionPersonnelPage")),
                                            handle: { help: { topicKey: "trial-institution-personnel" } }
                                        },
                                        {
                                            path: ":personnelId",
                                            handle: { help: null },
                                            children: [
                                                {
                                                    index: true,
                                                    element: (
                                                        <Navigate replace to="details" />
                                                    ),
                                                    handle: { help: null }
                                                },
                                                {
                                                    path: "details",
                                                    lazy: lazyRoute(() => import("../containers/TrialInstitution/TrialInstitutionPersonnel/TrialInstitutionPersonnelPage")),
                                                    handle: { help: { topicKey: "trial-institution-personnel" } }
                                                },
                                            ],
                                        },
                                    ],
                                },
                                {
                                    path: "notes",
                                    handle: { help: null },
                                    children: [
                                        {
                                            index: true,
                                            lazy: lazyRoute(() => import("../containers/TrialInstitution/TrialInstitutionNotes/TrialInstitutionNotesPage")),
                                            handle: { help: { topicKey: "trial-institution-notes" } }
                                        },
                                        {
                                            path: "create",
                                            lazy: lazyRoute(() => import("../containers/Notes/Note/NotePageSWR"), { kind: "trialInstitution", mode: "create" }),
                                            handle: { help: { topicKey: "note" } }
                                        },
                                        {
                                            path: ":noteId",
                                            handle: { help: null },
                                            children: [
                                                {
                                                    index: true,
                                                    element: (
                                                        <Navigate replace to="details" />
                                                    ),
                                                    handle: { help: null }
                                                },
                                                {
                                                    path: "details",
                                                    lazy: lazyRoute(() => import("../containers/Notes/Note/NotePageSWR"), { kind: "trialInstitution", mode: "edit" }),
                                                    handle: { help: { topicKey: "note" } }
                                                },
                                            ],
                                        },
                                    ],
                                },
                                {
                                    path: "labs",
                                    handle: { help: null },
                                    children: [
                                        {
                                            index: true,
                                            lazy: lazyRoute(() => import("../containers/TrialInstitution/TrialInstitutionLaboratories/TrialInstitutionLaboratoriesPage")),
                                            handle: { help: { topicKey: "trial-institution-laboratories" } }
                                        },
                                        {
                                            path: "create",
                                            lazy: lazyRoute(() => import("../containers/TrialInstitution/TrialInstitutionLaboratory/TrialInstitutionLaboratoryPage"), { kind: "trialInstitution", mode: "create" }),
                                            handle: { help: { topicKey: "trial-institution-laboratory" } }
                                        },
                                        {
                                            path: ":trialInstitutionLaboratoryId",
                                            handle: { help: null },
                                            children: [
                                                {
                                                    index: true,
                                                    element: (
                                                        <Navigate replace to="details" />
                                                    ),
                                                    handle: { help: null }
                                                },
                                                {
                                                    path: "details",
                                                    lazy: lazyRoute(() => import("../containers/TrialInstitution/TrialInstitutionLaboratory/TrialInstitutionLaboratoryPage"), { kind: "trialInstitution", mode: "edit" }),
                                                    handle: { help: { topicKey: "trial-institution-laboratory" } }
                                                },
                                                {
                                                    path: "personnel",
                                                    handle: { help: null },
                                                    children: [
                                                        {
                                                            index: true,
                                                            lazy: lazyRoute(() => import("../containers/TrialInstitution/TrialInstitutionLaboratoryPersonnels/TrialInstitutionLaboratoryPersonnelsPage")),
                                                                handle: { help: { topicKey: "trial-institution-laboratory-personnels" } }
                                                        },
                                                        {
                                                            path: "create",
                                                            lazy: lazyRoute(() => import("../containers/TrialInstitution/TrialInstitutionLaboratoryPersonnel/TrialInstitutionLaboratoryPersonnelPage")),
                                                                handle: { help: { topicKey: "trial-institution-laboratory-personnel" } }
                                                        },
                                                        {
                                                            path: ":trialInstitutionLaboratoryPersonnelId",
                                                            handle: { help: null },
                                                            children:
                                                                [
                                                                    {
                                                                        index: true,
                                                                        element:
                                                                            (
                                                                                <Navigate replace to="details" />
                                                                            ),
                                                                            handle: { help: null }
                                                                    },
                                                                    {
                                                                        path: "details",
                                                                        lazy: lazyRoute(() => import("../containers/TrialInstitution/TrialInstitutionLaboratoryPersonnel/TrialInstitutionLaboratoryPersonnelPage")),
                                                                            handle: { help: { topicKey: "trial-institution-laboratory-personnel" } }
                                                                    },
                                                                ],
                                                        },
                                                    ],
                                                },
                                                {
                                                    path: "finance",
                                                    lazy: lazyRoute(() => import("../containers/TrialInstitution/TrialInstitutionLaboratoryFinance/TrialInstitutionLaboratoryFinancePage")),
                                                    handle: { help: { topicKey: "trial-institution-laboratory-finance" } }
                                                },
                                                {
                                                    path: "contracts",
                                                    handle: { help: null },
                                                    children: [
                                                        {
                                                            index: true,
                                                            lazy: lazyRoute(() => import("../containers/TrialInstitution/TrialInstitutionLaboratoryContracts/TrialInstitutionLaboratoryContractsPage")),
                                                                handle: { help: { topicKey: "trial-institution-laboratory-contracts" } }
                                                        },
                                                        {
                                                            path: "create",
                                                            lazy: lazyRoute(() => import("../containers/TrialInstitution/TrialInstitutionLaboratoryContract/TrialInstitutionLaboratoryContractPage")),
                                                                handle: { help: { topicKey: "trial-institution-laboratory-contract" } }
                                                        },
                                                        {
                                                            path: ":trialInstitutionLaboratoryContractId",
                                                            handle: { help: null },
                                                            children:
                                                                [
                                                                    {
                                                                        index: true,
                                                                        element:
                                                                            (
                                                                                <Navigate replace to="details" />
                                                                            ),
                                                                            handle: { help: null }
                                                                    },
                                                                    {
                                                                        path: "details",
                                                                        lazy: lazyRoute(() => import("../containers/TrialInstitution/TrialInstitutionLaboratoryContract/TrialInstitutionLaboratoryContractPage")),
                                                                            handle: { help: { topicKey: "trial-institution-laboratory-contract" } }
                                                                    },
                                                                ],
                                                        },
                                                    ],
                                                },
                                            ],
                                        },
                                    ],
                                },
                                {
                                    path: "finance",
                                    lazy: lazyRoute(() => import("../containers/TrialInstitution/TrialInstitutionFinance/TrialInstitutionFinancePage")),
                                    handle: { help: { topicKey: "trial-institution-finance" } }
                                },
                                {
                                    path: "contracts",
                                    handle: {
                                        title: "Contracts",
                                        help: null,
                                        access: { permission: Dtos.Permission.TrialInstitutionContractView, trialParam: "trialId", institutionParam: "trialInstitutionId" },
                                        breadcrumb: { label: "Contracts", icon: FontAwesomeIcons.Duotone.FILECONTRACT, url: ({ params }) => `/trials/${params.trialId}/trialinstitution/${params.trialInstitutionId}/contracts` },
                                    },
                                    children: [
                                        {
                                            index: true,
                                            lazy: lazyRoute(() => import("../containers/TrialInstitution/TrialInstitutionContracts/TrialInstitutionContractsPage")),
                                            handle: {
                                                title: "Contracts",
                                                help: { topicKey: "trial-institution-contracts" },
                                                access: { permission: Dtos.Permission.TrialInstitutionContractView, trialParam: "trialId", institutionParam: "trialInstitutionId" },
                                                breadcrumb: null,
                                            }
                                        },
                                        {
                                            path: "create",
                                            lazy: lazyRoute(() => import("../containers/Trials/TrialInstitutionContract/TrialInstitutionContractPage")),
                                            handle: {
                                                title: "Create Contract",
                                                help: { topicKey: "trial-institution-contract" },
                                                access: { permission: Dtos.Permission.TrialInstitutionContractCreate, trialParam: "trialId", institutionParam: "trialInstitutionId" },
                                                breadcrumb: { label: "Create", icon: FontAwesomeIcons.Duotone.SQUAREPLUS, url: ({ params }) => `/trials/${params.trialId}/trialinstitution/${params.trialInstitutionId}/contracts/create` },
                                            }
                                        },
                                        {
                                            path: ":trialInstitutionContractId",
                                            handle: {
                                                title: ({ labels }) => labels.trialInstitutionContractName ?? "Contract",
                                                help: null,
                                                access: { permission: Dtos.Permission.TrialInstitutionContractView, trialParam: "trialId", institutionParam: "trialInstitutionId" },
                                                breadcrumb: { label: ({ labels }) => labels.trialInstitutionContractName ?? "Contract", icon: FontAwesomeIcons.Duotone.FILECONTRACT, url: ({ params }) => `/trials/${params.trialId}/trialinstitution/${params.trialInstitutionId}/contracts/${params.trialInstitutionContractId}/details` },
                                            },
                                            children: [
                                                {
                                                    index: true,
                                                    element: (
                                                        <Navigate replace to="details" />
                                                    ),
                                                    handle: { title: ({ labels }) => labels.trialInstitutionContractName ?? "Contract", help: null, breadcrumb: null }
                                                },
                                                {
                                                    path: "details",
                                                    lazy: lazyRoute(() => import("../containers/Trials/TrialInstitutionContract/TrialInstitutionContractPage")),
                                                    handle: {
                                                        title: ({ labels }) => labels.trialInstitutionContractName ?? "Contract",
                                                        help: { topicKey: "trial-institution-contract" },
                                                        access: { permission: Dtos.Permission.TrialInstitutionContractView, trialParam: "trialId", institutionParam: "trialInstitutionId" },
                                                        breadcrumb: null,
                                                    }
                                                },
                                            ],
                                        },
                                    ],
                                },
                                {
                                    path: "monitoring",
                                    handle: { help: null },
                                    children: [
                                        {
                                            index: true,
                                            lazy: lazyRoute(() => import("../containers/Monitoring/Monitorings/MonitoringsPage")),
                                            handle: { help: { topicKey: "monitorings" } }
                                        },
                                        {
                                            path: "create",
                                            lazy: lazyRoute(() => import("../containers/Monitoring/Monitoring/MonitoringPage")),
                                            handle: { help: { topicKey: "monitoring" } }
                                        },
                                        {
                                            path: ":monitoringId",
                                            handle: { help: null, breadcrumb: { label: ({ labels, params }) => labels.monitoringName ?? `Visit #${params.monitoringId}`, icon: FontAwesomeIcons.Duotone.MONITOR, url: ({ params }) => `/trials/${params.trialId}/trialinstitution/${params.trialInstitutionId}/monitoring/${params.monitoringId}/details` } },
                                            children: [
                                                {
                                                    index: true,
                                                    element: (
                                                        <Navigate replace to="details" />
                                                    ),
                                                    handle: { help: null }
                                                },
                                                {
                                                    path: "details",
                                                    lazy: lazyRoute(() => import("../containers/Monitoring/Monitoring/MonitoringPage")),
                                                    handle: { help: { topicKey: "monitoring" } }
                                                },
                                                {
                                                    path: "documents",
                                                    handle: { help: null },
                                                    children: [
                                                        {
                                                            index: true,
                                                            lazy: lazyRoute(() => import("../containers/Monitoring/MonitoringDocuments/MonitoringDocumentsPage")),
                                                            handle: { help: { topicKey: "monitoring-documents" } }
                                                        },
                                                        {
                                                            path: "create",
                                                            lazy: lazyRoute(() => import("../containers/Monitoring/MonitoringDocument/MonitoringDocumentPage")),
                                                            handle: { help: { topicKey: "monitoring-document" } }
                                                        },
                                                        {
                                                            path: ":monitoringDocumentId",
                                                            handle: { help: null, breadcrumb: { label: ({ labels, params }) => labels.monitoringDocumentName ?? `Document #${params.monitoringDocumentId}`, icon: FontAwesomeIcons.Duotone.FILE, url: ({ params }) => `/trials/${params.trialId}/trialinstitution/${params.trialInstitutionId}/monitoring/${params.monitoringId}/documents/${params.monitoringDocumentId}/details` } },
                                                            children: [
                                                                {
                                                                    index: true,
                                                                    element: (
                                                                        <Navigate replace to="details" />
                                                                    ),
                                                                    handle: { help: null }
                                                                },
                                                                {
                                                                    path: "details",
                                                                    lazy: lazyRoute(() => import("../containers/Monitoring/MonitoringDocument/MonitoringDocumentPage")),
                                                                    handle: { help: { topicKey: "monitoring-document" } }
                                                                }
                                                            ]
                                                        }
                                                    ]
                                                },
                                                {
                                                    path: "issues",
                                                    handle: { help: { topicKey: "monitoring-issues" } },
                                                    children: [
                                                        {
                                                            index: true,
                                                            lazy: lazyRoute(() => import("../containers/Monitoring/MonitoringIssues/MonitoringIssuesPage")),
                                                            handle: { help: { topicKey: "monitoring-issues" } }
                                                        },
                                                        {
                                                            path: "create",
                                                            lazy: lazyRoute(() => import("../containers/Monitoring/MonitoringIssue/MonitoringIssuePage")),
                                                            handle: { help: { topicKey: "monitoring-issue" } }
                                                        },
                                                        {
                                                            path: ":monitoringIssueId",
                                                            handle: { help: null, breadcrumb: { label: ({ params }) => `Issue #${params.monitoringIssueId}`, icon: FontAwesomeIcons.Duotone.MONITORINGISSUE, url: ({ params }) => `/trials/${params.trialId}/trialinstitution/${params.trialInstitutionId}/monitoring/${params.monitoringId}/issues/${params.monitoringIssueId}/details` } },
                                                            children: [
                                                                {
                                                                    index: true,
                                                                    element: (
                                                                        <Navigate replace to="details" />
                                                                    ),
                                                                    handle: { help: null }
                                                                },
                                                                {
                                                                    path: "details",
                                                                    lazy: lazyRoute(() => import("../containers/Monitoring/MonitoringIssue/MonitoringIssuePage")),
                                                                    handle: { help: { topicKey: "monitoring-issue" } }
                                                                },
                                                                {
                                                                    path: "documents",
                                                                    handle: { help: null },
                                                                    children: [
                                                                        {
                                                                            index: true,
                                                                            lazy: lazyRoute(() => import("../containers/Monitoring/MonitoringIssueDocuments/MonitoringIssueDocumentsPage")),
                                                                            handle: { help: { topicKey: "monitoring-issue-documents" } }
                                                                        },
                                                                        {
                                                                            path: "create",
                                                                            lazy: lazyRoute(() => import("../containers/Monitoring/MonitoringIssueDocument/MonitoringIssueDocumentPage")),
                                                                            handle: { help: { topicKey: "monitoring-issue-document" } }
                                                                        },
                                                                        {
                                                                            path: ":monitoringIssueDocumentId",
                                                                            handle: { help: null, breadcrumb: { label: ({ labels, params }) => labels.monitoringIssueDocumentName ?? `Document #${params.monitoringIssueDocumentId}`, icon: FontAwesomeIcons.Duotone.FILE, url: ({ params }) => `/trials/${params.trialId}/trialinstitution/${params.trialInstitutionId}/monitoring/${params.monitoringId}/issues/${params.monitoringIssueId}/documents/${params.monitoringIssueDocumentId}/details` } },
                                                                            children: [
                                                                                {
                                                                                    index: true,
                                                                                    element: (
                                                                                        <Navigate replace to="details" />
                                                                                    ),
                                                                                    handle: { help: null }
                                                                                },
                                                                                {
                                                                                    path: "details",
                                                                                    lazy: lazyRoute(() => import("../containers/Monitoring/MonitoringIssueDocument/MonitoringIssueDocumentPage")),
                                                                                    handle: { help: { topicKey: "monitoring-issue-document" } }
                                                                                }
                                                                            ]
                                                                        }
                                                                    ]
                                                                },
                                                            ]
                                                        }
                                                    ]
                                                }
                                            ],
                                        },
                                    ],
                                },
                            ],
                        },
                    ],
                },
                {
                    path: "trialinstitutionitemviews",
                    handle: {
                        title: "Custom Views",
                        help: null,
                        access: { permission: Dtos.Permission.TrialItemView, trialParam: "trialId" },
                        breadcrumb: { label: "Custom Views", icon: FontAwesomeIcons.Duotone.TALLY, url: ({ params }) => `/trials/${params.trialId}/trialinstitutionitemviews` },
                    },
                    children: [
                        {
                            index: true,
                            lazy: lazyRoute(() => import("../containers/Trials/TrialInstitutionItemCustomViews/TrialInstitutionItemCustomViewsPage")),
                            handle: {
                                title: "Custom Views",
                                help: { topicKey: "trial-institution-item-custom-views" },
                                access: { permission: Dtos.Permission.TrialItemView, trialParam: "trialId" },
                                breadcrumb: null,
                            }
                        },
                        {
                            path: ":trialInstitutionItemCustomViewId/dataentry",
                            lazy: lazyRoute(() => import("../containers/Trials/TrialInstitutionItemCustomViewDataEntry/TrialInstitutionItemCustomViewDataEntryPage")),
                            handle: { help: { topicKey: "trial-institution-item-custom-view-data-entry" } }
                        },
                        {
                            path: "create",
                            lazy: lazyRoute(() => import("../containers/Trials/TrialInstitutionItemCustomView/TrialInstitutionItemCustomViewPage"), { mode: "create" }),
                            handle: { help: { topicKey: "trial-institution-item-custom-view" } }
                        },
                        {
                            path: ":trialInstitutionItemCustomViewId",
                            lazy: lazyRoute(() => import("../containers/Trials/TrialInstitutionItemCustomView/TrialInstitutionItemCustomViewPage"), { mode: "edit" }),
                            handle: { help: { topicKey: "trial-institution-item-custom-view" } }
                        },
                    ],
                },
                {
                    path: "trialitems",
                    handle: { help: { topicKey: "trial-items" } },
                    children: [
                        {
                            index: true,
                            lazy: lazyRoute(() => import("../containers/Trials/TrialItems/TrialItemsPageSWR")),
                            handle: { help: { topicKey: "trial-items" } }
                        }
                    ],
                },
                {
                    path: "personnel",
                    handle: { help: null },
                    children: [
                        {
                            index: true,
                            lazy: lazyRoute(() => import("../containers/Trials/TrialPersonnels/TrialPersonnelsPage")),
                            handle: { help: { topicKey: "trial-personnel" } }
                        },
                        {
                            path: "create",
                            lazy: lazyRoute(() => import("../containers/Trials/TrialPersonnel/TrialPersonnelPage")),
                            handle: { help: { topicKey: "trial-personnel" } }
                        },
                        {
                            path: ":trialPersonnelId",
                            handle: { help: null },
                            children: [
                                {
                                    index: true,
                                    element: (
                                        <Navigate replace to="details" />
                                    ),
                                    handle: { help: null }
                                },
                                {
                                    path: "details",
                                    lazy: lazyRoute(() => import("../containers/Trials/TrialPersonnel/TrialPersonnelPage")),
                                    handle: { help: { topicKey: "trial-personnel" } }
                                },
                            ],
                        },
                    ],
                },
                {
                    path: "accrual",
                    lazy: lazyRoute(() => import("../containers/Trials/TrialAccruals/TrialAccrualsPage")),
                    handle: { help: { topicKey: "trial-accruals" } }
                },
                {
                    path: "notes",
                    handle: { help: null },
                    children: [
                        {
                            index: true,
                            lazy: lazyRoute(() => import("../containers/Trials/TrialNotes/TrialNotesPage")),
                            handle: { help: { topicKey: "trial-notes" } }
                        },
                        {
                            path: "create",
                            lazy: lazyRoute(() => import("../containers/Notes/Note/NotePageSWR"), { kind: "trial", mode: "create" }),
                            handle: { help: { topicKey: "note" } }
                        },
                        {
                            path: ":noteId",
                            handle: { help: null },
                            children: [
                                {
                                    index: true,
                                    element: (
                                        <Navigate replace to="details" />
                                    ),
                                    handle: { help: null }
                                },
                                {
                                    path: "details",
                                    lazy: lazyRoute(() => import("../containers/Notes/Note/NotePageSWR"), { kind: "trial", mode: "edit" }),
                                    handle: { help: { topicKey: "note" } }
                                },
                            ],
                        },
                    ],
                },
                {
                    path: "groups",
                    handle: { help: null },
                    children: [
                        {
                            index: true,
                            lazy: lazyRoute(() => import("../containers/TrialGroup/TrialGroups/TrialGroupsPageSWR")),
                            handle: { help: { topicKey: "trial-groups" } }
                        },
                        {
                            path: ":groupId",
                            handle: { help: null },
                            children: [
                                {
                                    path: "trialgroup",
                                    handle: { help: null, breadcrumb: null },
                                    children: [
                                        {
                                            path: ":trialGroupId",
                                            handle: { help: { topicKey: "dashboard" }, breadcrumb: null },
                                            children: [
                                                {
                                                    index: true,
                                                    element: (
                                                        <Navigate replace to="details" />
                                                    ),
                                                    handle: { help: null }
                                                },
                                                {
                                                    path: "details",
                                                    lazy: lazyRoute(() => import("../containers/TrialGroup/TrialGroupPage/TrialGroupPageSWR")),
                                                    handle: { help: { topicKey: "trial-group" } }
                                                },
                                                {
                                                    path: "personnel",
                                                    handle: { help: null },
                                                    children: [
                                                        {
                                                            index: true,
                                                            lazy: lazyRoute(() => import("../containers/TrialGroup/TrialGroupPersonnels/TrialGroupPersonnelsPage")),
                                                                handle: { help: { topicKey: "trial-group-personnels" } }
                                                        },
                                                        {
                                                            path: "create",
                                                            lazy: lazyRoute(() => import("../containers/TrialGroup/TrialGroupPersonnel/TrialGroupPersonnelPage")),
                                                            handle: { help: { topicKey: "trial-group-personnel" } }
                                                        },
                                                        {
                                                            path: ":trialPersonnelId",
                                                            handle: { help: null },
                                                            children:
                                                                [
                                                                    {
                                                                        index: true,
                                                                        element:
                                                                            (
                                                                                <Navigate replace to="details" />
                                                                            ),
                                                                            handle: { help: null }
                                                                    },
                                                                    {
                                                                        path: "details",
                                                                        lazy: lazyRoute(() => import("../containers/TrialGroup/TrialGroupPersonnel/TrialGroupPersonnelPage")),
                                                                        handle: { help: { topicKey: "trial-group-personnel" } }
                                                                    },
                                                                ],
                                                        },
                                                    ],
                                                },
                                                {
                                                    path: "items",
                                                    lazy: lazyRoute(() => import("../containers/TrialGroup/TrialGroupItems/TrialGroupItemsPageSWR")),
                                                    handle: { help: { topicKey: "trial-group-items" } }
                                                },
                                                {
                                                    path: "labs",
                                                    handle: { help: null },
                                                    children: [
                                                        {
                                                            index: true,
                                                            lazy: lazyRoute(() => import("../containers/TrialGroup/TrialGroupLaboratories/TrialGroupLaboratoriesPage")),
                                                            handle: { help: { topicKey: "trial-group-laboratories" } }
                                                        },
                                                        {
                                                            path: "create",
                                                            lazy: lazyRoute(() => import("../containers/TrialGroup/TrialGroupLaboratory/TrialGroupLaboratoryPage"), { kind: "trialGroup", routeOrientation: "trial", mode: "create" }),
                                                            handle: { help: { topicKey: "trial-group-laboratory" } }
                                                        },
                                                        {
                                                            path: ":trialGroupLaboratoryId",
                                                            handle: { help: null },
                                                            children:
                                                                [
                                                                    {
                                                                        index: true,
                                                                        element:
                                                                            (
                                                                                <Navigate replace to="details" />
                                                                            ),
                                                                            handle: { help: null }
                                                                    },
                                                                    {
                                                                        path: "details",
                                                                        lazy: lazyRoute(() => import("../containers/TrialGroup/TrialGroupLaboratory/TrialGroupLaboratoryPage"), { kind: "trialGroup", routeOrientation: "trial", mode: "edit" }),
                                                                        handle: { help: { topicKey: "trial-group-laboratory" } }
                                                                    },
                                                                    {
                                                                        path: "personnel",
                                                                        handle: { help: null },
                                                                        children:
                                                                            [
                                                                                {
                                                                                    index: true,
                                                                                    lazy: lazyRoute(() => import("../containers/TrialGroup/TrialGroupLaboratoryPersonnels/TrialGroupLaboratoryPersonnelsPage")),
                                                                                    handle: { help: { topicKey: "trial-group-laboratory-personnels" } }
                                                                                },
                                                                                {
                                                                                    path: "create",
                                                                                    lazy: lazyRoute(() => import("../containers/TrialGroup/TrialGroupLaboratoryPersonnel/TrialGroupLaboratoryPersonnelPage")),
                                                                                    handle: { help: { topicKey: "trial-group-laboratory-personnel" } }
                                                                                },
                                                                                {
                                                                                    path: ":trialGroupLaboratoryPersonnelId",
                                                                                    handle: { help: null },
                                                                                    children:
                                                                                        [
                                                                                            {
                                                                                                index: true,
                                                                                                element:
                                                                                                    (
                                                                                                        <Navigate replace to="details" />
                                                                                                    ),
                                                                                                    handle: { help: null }
                                                                                            },
                                                                                            {
                                                                                                path: "details",
                                                                                                lazy: lazyRoute(() => import("../containers/TrialGroup/TrialGroupLaboratoryPersonnel/TrialGroupLaboratoryPersonnelPage")),
                                                                                                handle: { help: { topicKey: "trial-group-laboratory-personnel" } }
                                                                                            },
                                                                                        ],
                                                                                },
                                                                            ],
                                                                    },
                                                                    {
                                                                        path: "finance",
                                                                        lazy: lazyRoute(() => import("../containers/TrialGroup/TrialGroupLaboratoryFinance/TrialGroupLaboratoryFinancePage")),
                                                                        handle: { help: { topicKey: "trial-group-laboratory-finance" } }
                                                                    },
                                                                    {
                                                                        path: "contracts",
                                                                        handle: { help: null },
                                                                        children:
                                                                            [
                                                                                {
                                                                                    index: true,
                                                                                    lazy: lazyRoute(() => import("../containers/TrialGroup/TrialGroupLaboratoryContracts/TrialGroupLaboratoryContractsPage")),
                                                                                    handle: { help: { topicKey: "trial-group-laboratory-contracts" } }
                                                                                },
                                                                                {
                                                                                    path: "create",
                                                                                    lazy: lazyRoute(() => import("../containers/TrialGroup/TrialGroupLaboratoryContract/TrialGroupLaboratoryContractPage")),
                                                                                    handle: { help: { topicKey: "trial-group-laboratory-contract" } }
                                                                                },
                                                                                {
                                                                                    path: ":trialGroupLaboratoryContractId",
                                                                                    handle: { help: null },
                                                                                    children:
                                                                                        [
                                                                                            {
                                                                                                index: true,
                                                                                                element:
                                                                                                    (
                                                                                                        <Navigate replace to="details" />
                                                                                                    ),
                                                                                                    handle: { help: null }
                                                                                            },
                                                                                            {
                                                                                                path: "details",
                                                                                                lazy: lazyRoute(() => import("../containers/TrialGroup/TrialGroupLaboratoryContract/TrialGroupLaboratoryContractPage")),
                                                                                                handle: { help: { topicKey: "trial-group-laboratory-contract" } }
                                                                                            },
                                                                                        ],
                                                                                },
                                                                            ],
                                                                    },
                                                                ],
                                                        },
                                                    ],
                                                },
                                                {
                                                    path: "finance",
                                                    lazy: lazyRoute(() => import("../containers/TrialGroup/TrialGroupFinance/TrialGroupFinancePage")),
                                                    handle: { help: { topicKey: "trial-group-finance" } }
                                                },
                                                {
                                                    path: "contracts",
                                                    handle: { help: null },
                                                    children: [
                                                        {
                                                            index: true,
                                                            lazy: lazyRoute(() => import("../containers/TrialGroup/TrialGroupContracts/TrialGroupContractsPage")),
                                                            handle: { help: { topicKey: "trial-group-contracts" } }
                                                        },
                                                        {
                                                            path: "create",
                                                            lazy: lazyRoute(() => import("../containers/TrialGroup/TrialGroupContract/TrialGroupContractPage")),
                                                            handle: { help: { topicKey: "trial-group-contract" } }
                                                        },
                                                        {
                                                            path: ":trialGroupContractId",
                                                            handle: { help: null },
                                                            children:
                                                                [
                                                                    {
                                                                        index: true,
                                                                        element:
                                                                            (
                                                                                <Navigate replace to="details" />
                                                                            ),
                                                                            handle: { help: null }
                                                                    },
                                                                    {
                                                                        path: "details",
                                                                        lazy: lazyRoute(() => import("../containers/TrialGroup/TrialGroupContract/TrialGroupContractPage")),
                                                                        handle: { help: { topicKey: "trial-group-contract" } }
                                                                    },
                                                                ],
                                                        },
                                                    ],
                                                },
                                            ],
                                        },
                                    ],
                                },
                            ],
                        },
                        {
                            path: ":trialGroupId",
                            handle: { help: { topicKey: "dashboard" } },
                            children: [
                                {
                                    path: "details",
                                    lazy: lazyRoute(() => import("../containers/TrialGroup/TrialGroupPage/TrialGroupPageSWR")),
                                    handle: { help: { topicKey: "trial-group" } }
                                },
                            ],
                        },
                    ],
                },
                {
                    path: "datasharing",
                    lazy: lazyRoute(() => import("../containers/Trials/TrialDoi/TrialDoiPage")),
                    handle: {
                        title: "Data Sharing",
                        help: { topicKey: "trial-doi" },
                        access: { permission: Dtos.Permission.TrialDoiView, trialParam: "trialId" },
                        breadcrumb: { label: "Data Sharing", icon: FontAwesomeIcons.Duotone.GLOBE, url: ({ params }) => `/trials/${params.trialId}/datasharing` },
                    }
                },
                {
                    path: "website",
                    lazy: lazyRoute(() => import("../containers/Trials/TrialWebsite/TrialWebsitePage")),
                    handle: {
                        title: "Website",
                        help: { topicKey: "trial-website" },
                        access: { permission: Dtos.Permission.TrialWebsiteView, trialParam: "trialId" },
                        breadcrumb: { label: "Website", icon: FontAwesomeIcons.Duotone.GLOBE, url: ({ params }) => `/trials/${params.trialId}/website` },
                    }
                },
                {
                    path: "finance",
                    lazy: lazyRoute(() => import("../containers/Trials/TrialFinance/TrialFinancePage")),
                    handle: {
                        title: "Finance",
                        help: { topicKey: "trial-finance" },
                        access: { permission: Dtos.Permission.TrialFinanceView, trialParam: "trialId" },
                        breadcrumb: { label: "Finance", icon: FontAwesomeIcons.Duotone.MONEYCHECKDOLLARPEN, url: ({ params }) => `/trials/${params.trialId}/finance` },
                    }
                },
                {
                    path: "labs",
                    lazy: lazyRoute(() => import("../containers/Trials/TrialLaboratories/TrialLaboratoriesPage")),
                    handle: {
                        title: "Laboratories",
                        help: { topicKey: "trial-laboratories" },
                        access: { permission: Dtos.Permission.TrialLaboratoryView, trialParam: "trialId" },
                        breadcrumb: { label: "Laboratories", icon: FontAwesomeIcons.Duotone.FLASKVIAL, url: ({ params }) => `/trials/${params.trialId}/labs` },
                    }
                },
            ],
        },
        {
            path: ":projectId",
            handle: { help: null },
            children: [
                {
                    path: "project",
                    handle: {
                        help: null,
                        navigation: { backTo: ({ params }) => `/trials/${params.projectId}/details` },
                    },
                    children: [
                        {
                            path: "details",
                            lazy: lazyRoute(() => import("../containers/Projects/Project/ProjectPage"), { context: "trial", mode: "edit" }),
                            handle: { help: { topicKey: "project" } }
                        },
                        {
                            path: "investigators",
                            handle: { help: null },
                            children: [
                                {
                                    path: ":projectInvestigatorId",
                                    handle: { help: null },
                                    children: [
                                        {
                                            index: true,
                                            element: (
                                                <Navigate replace to="details" />
                                            ),
                                            handle: { help: null }
                                        },
                                        {
                                            path: "details",
                                            lazy: lazyRoute(() => import("../containers/Projects/ProjectInvestigator/ProjectInvestigatorPage"), { context: "trial", mode: "edit" }),
                                            handle: { help: { topicKey: "project-investigator" } }
                                        },
                                    ],
                                },
                            ],
                        },
                        {
                            path: "discussions",
                            handle: { help: null, access: { permission: Dtos.Permission.ProjectDiscussionView } },
                            children: [
                                {
                                    index: true,
                                    lazy: lazyRoute(() => import("../containers/Projects/Discussions/DiscussionsPageSWR")),
                                    handle: { help: { topicKey: "discussions" } }
                                },
                                {
                                    path: "create",
                                    lazy: lazyRoute(() => import("../containers/Projects/Discussion/DiscussionPageSWR"), { orientation: "trial", mode: "create" }),
                                    handle: { help: { topicKey: "discussion" } }
                                },
                                {
                                    path: ":discussionId",
                                    handle: { help: null },
                                    children: [
                                        {
                                            index: true,
                                            element: (
                                                <Navigate replace to="details" />
                                            ),
                                            handle: { help: null }
                                        },
                                        {
                                            path: "details",
                                            lazy: lazyRoute(() => import("../containers/Projects/Discussion/DiscussionPageSWR"), { orientation: "trial", mode: "edit" }),
                                            handle: { help: { topicKey: "discussion" } }
                                        },
                                    ],
                                },
                            ],
                        },
                        {
                            path: "items",
                            lazy: lazyRoute(() => import("../containers/Projects/ProjectItems/ProjectItemsPageSWR")),
                            handle: { help: { topicKey: "project-items" } }
                        },
                    ],
                },
            ],
        },
    ],
} satisfies AppRouteObject;

